// src/pages/PatientOverviewPage.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
// Chart.js
import { Chart as ChartJS, registerables } from "chart.js";
import { Line } from "react-chartjs-2";
import { MinMaxBarsPlugin } from "../utils/results/plugins/minMaxBars";

ChartJS.register(...registerables, MinMaxBarsPlugin);

// Tailwind primary colour (same as cohort page)
const PRIMARY_COLOR = "#852170";
const TERTIARY_COLOR = "#B776B9";
const OUTLIER_COLOR = "#F97316"; // orange for predicted outliers

// Which metrics we care about + how to read/label them
const METRIC_CONFIG = [
  {
    key: "rr_median_ipi",
    label: "Respiratory Rate",
    field: "rr_median_ipi",
    unit: "brpm",
    defaultSelected: true,
    decimals: 0,
  },
  {
    key: "ratio",
    label: "FEV1/FVC Ratio",
    field: "ratio",
    unit: "",
    decimals: 2,
  },
  {
    key: "fev1",
    label: "FEV1",
    field: "fev1",
    unit: "L",
    decimals: 2,
  },
  {
    key: "fvc",
    label: "FVC",
    field: "fvc",
    unit: "L",
    decimals: 2,
  },
  //   {
  //     key: "rr_count",
  //     label: "Respiratory Rate",
  //     field: "rr_count",
  //     unit: "brpm",
  //     decimals: 0,
  //   },
];

// Small helper: get YYYY-MM-DD from timestamp, safely
function dayKeyFromTimestamp(ts) {
  if (!ts || typeof ts !== "string") return null;
  // Assumes ts is ISO-like: "YYYY-MM-DD..."
  return ts.slice(0, 10);
}

// Tiny helper to format dates nicely
function formatDate(d) {
  if (!d) return "--";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return "--";
  return dt.toLocaleDateString("en-GB");
}
// Trend classifier based on the same kind of linear regression
// used for the rough path line, so table + chart agree.
function classifyTrend(values) {
  if (!values || values.length < 2)
    return { icon: "trending_flat", label: "Stable" };

  // Filter to finite numbers
  const ys = values.filter((v) => Number.isFinite(v));
  if (ys.length < 2) return { icon: "trending_flat", label: "Stable" };

  const n = ys.length;

  // x = 0..n-1 (equally spaced in time)
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;

  ys.forEach((y, i) => {
    const x = i;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  });

  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return { icon: "trending_flat", label: "Stable" };

  const m = (n * sumXY - sumX * sumY) / denom; // slope
  const b = (sumY - m * sumX) / n; // intercept

  // Compare start vs end of the *fitted* line
  const startY = b; // x = 0
  const endY = m * (n - 1) + b; // x = n-1
  const diff = endY - startY;
  const pct = (diff / Math.abs(startY || 1)) * 100;

  if (pct > 5) return { icon: "trending_up", label: "Increasing" };
  if (pct < -5) return { icon: "trending_down", label: "Decreasing" };
  return { icon: "trending_flat", label: "Stable" };
}

// Compute robust global centre + scale (median + MAD) for a metric
function computeGlobalRobustStats(values) {
  const clean = values.filter(
    (v) => typeof v === "number" && Number.isFinite(v)
  );
  if (clean.length < 3) return null;

  const sorted = clean.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];

  const absDevs = clean.map((v) => Math.abs(v - median));
  const sortedDevs = absDevs.slice().sort((a, b) => a - b);
  const midDev = Math.floor(sortedDevs.length / 2);
  const mad =
    sortedDevs.length % 2 === 0
      ? (sortedDevs[mid - 1] + sortedDevs[mid]) / 2
      : sortedDevs[mid];

  if (!mad || !Number.isFinite(mad)) return null;

  return { median, mad };
}

// Split values into inliers / outliers using a robust MAD rule.
// If `globalStats` is provided, we use its median + MAD; otherwise we fall
// back to estimating from `vals` themselves.
function splitOutliers(vals, globalStats = null, threshold = 3.5) {
  if (!vals || vals.length === 0) {
    return { inliers: [], outliers: [] };
  }
  if (vals.length < 3 && !globalStats) {
    // With very few points and no global stats, don't try to classify
    return { inliers: vals.slice(), outliers: [] };
  }

  let median;
  let mad;

  if (
    globalStats &&
    Number.isFinite(globalStats.median) &&
    Number.isFinite(globalStats.mad) &&
    globalStats.mad > 0
  ) {
    ({ median, mad } = globalStats);
  } else {
    // Local robust stats from this day's values
    const sorted = vals.slice().sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    median =
      sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];

    const absDevs = vals.map((v) => Math.abs(v - median));
    const sortedDevs = absDevs.slice().sort((a, b) => a - b);
    const midDev = Math.floor(sortedDevs.length / 2);
    mad =
      sortedDevs.length % 2 === 0
        ? (sortedDevs[midDev - 1] + sortedDevs[midDev]) / 2
        : sortedDevs[midDev];
  }

  if (!mad || !Number.isFinite(mad)) {
    return { inliers: vals.slice(), outliers: [] };
  }

  const inliers = [];
  const outliers = [];

  vals.forEach((v) => {
    const robZ = (0.6745 * Math.abs(v - median)) / mad;
    if (robZ > threshold) outliers.push(v);
    else inliers.push(v);
  });

  if (!inliers.length) {
    // Safety: if everything got flagged, treat all as inliers
    return { inliers: vals.slice(), outliers: [] };
  }

  return { inliers, outliers };
}

export default function PatientOverviewPage({
  rows,
  selectedPatient,
  setSelectedPatient,
}) {
  const navigate = useNavigate();

  const [sessions, setSessions] = useState(null); // [{ timestamp, fev1, fvc, ratio, rr_count, ... }]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const defaultMetricKey =
    METRIC_CONFIG.find((m) => m.defaultSelected)?.key || METRIC_CONFIG[0].key;
  const [selectedMetricKey, setSelectedMetricKey] = useState(defaultMetricKey);

  // If we end up here with no selected patient at all, bounce back
  useEffect(() => {
    if (!selectedPatient && !loading && !error) {
      // Only redirect if we don't have a patient and we're not in the middle of a load
      navigate("/patients");
    }
  }, [selectedPatient, loading, error, navigate]);

  // Fetch per-assessment results for the selected patient
  useEffect(() => {
    if (!selectedPatient?.uid) {
      setSessions(null);
      return;
    }

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const functions = getFunctions(undefined, "europe-west2");
        const fn = httpsCallable(functions, "patientResults");
        const res = await fn({ patientUid: selectedPatient.uid });

        console.log(res.data);

        // Expecting res.data.sessions = [{ timestamp, fev1, fvc, ratio, rr_count, ... }]
        const list = (res.data?.sessions || []).map((s) => ({
          ...s,
          // normalise timestamp field
          timestamp: s.timestamp || s.record_start_iso_utc || s.startedAt,
        }));

        // sort by time ascending
        list.sort((a, b) => {
          const ta = new Date(a.timestamp).getTime();
          const tb = new Date(b.timestamp).getTime();
          return ta - tb;
        });

        setSessions(list);
      } catch (err) {
        console.error("Failed to fetch patient results", err);
        setError(
          err?.message || "Something went wrong while loading patient results."
        );
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [selectedPatient?.uid]);

  // All metric summary rows
  const metricRows = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    return METRIC_CONFIG.map((m) => {
      const field = m.field;
      const bucketsMap = new Map();

      for (const s of sessions) {
        const raw = s[field];

        if (typeof raw !== "number" || !Number.isFinite(raw)) continue;
        if (!s.timestamp) continue;

        const key = dayKeyFromTimestamp(s.timestamp);
        if (!key) continue;

        if (!bucketsMap.has(key)) {
          // store the day as a Date purely for display, but don't use it as a key
          bucketsMap.set(key, { date: new Date(s.timestamp), values: [] });
        }
        bucketsMap.get(key).values.push(raw);
      }

      const buckets = Array.from(bucketsMap.values());
      if (!buckets.length) {
        return {
          ...m,
          hasData: false,
          currentAvg: null,
          measuredLast: null,
          trend: { icon: "trending_flat", label: "No data" },
        };
      }

      // Sort by date
      buckets.sort((a, b) => a.date.getTime() - b.date.getTime());

      // Daily averages in temporal order (these are what the chart plots)
      const dailyAvgs = buckets.map((b) => {
        const vals = b.values;
        const sum = vals.reduce((acc, v) => acc + v, 0);
        return sum / vals.length;
      });

      // Use the daily series for the trend classification so it matches the chart
      const trend = classifyTrend(dailyAvgs);

      // Overall average for the "Current (avg)" column
      const overallSum = dailyAvgs.reduce((acc, v) => acc + v, 0);
      const overallAvg = overallSum / dailyAvgs.length;

      // Last day with any data
      const lastDate = buckets[buckets.length - 1].date;

      return {
        ...m,
        hasData: true,
        currentAvg: overallAvg,
        measuredLast: lastDate, // formatDate can handle Date objects
        trend,
      };
    });
  }, [sessions]);

  const selectedMetricConfig = useMemo(
    () => METRIC_CONFIG.find((m) => m.key === selectedMetricKey),
    [selectedMetricKey]
  );

  // Global robust stats for the currently selected metric
  const globalStats = useMemo(() => {
    if (!sessions || !selectedMetricConfig) return null;

    const field = selectedMetricConfig.field;
    const allVals = sessions
      .map((s) => s[field])
      .filter((v) => typeof v === "number" && Number.isFinite(v));

    return computeGlobalRobustStats(allVals);
  }, [sessions, selectedMetricConfig]);

  // Chart data for the selected metric:
  // - one category per day between first and last measurement
  // - avg per day as the point
  // - min/max per day for the vertical bars
  // - plus a rough trend line if ≥ 2 points
  const chartData = useMemo(() => {
    if (!sessions || !selectedMetricConfig) return null;

    const field = selectedMetricConfig.field;

    const bucketsMap = new Map();

    for (const s of sessions) {
      const raw = s[field];

      if (typeof raw !== "number" || !Number.isFinite(raw)) continue;
      if (!s.timestamp) continue;

      const key = dayKeyFromTimestamp(s.timestamp);
      if (!key) continue;

      if (!bucketsMap.has(key)) {
        bucketsMap.set(key, { key, date: new Date(s.timestamp), values: [] });
      }
      bucketsMap.get(key).values.push(raw);
    }

    const buckets = Array.from(bucketsMap.values());
    if (buckets.length === 0) return null;

    // Sort by the *string* key (pure calendar order), or by date – both are fine
    buckets.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));

    // Build the full range using YYYY-MM-DD keys, not Date.setHours + toISOString
    const allKeys = buckets.map((b) => b.key);
    const startKey = allKeys[0]; // e.g. "2025-10-07"
    const endKey = allKeys[allKeys.length - 1];

    const labels = [];
    const values = [];
    const minMax = [];
    const rawPoints = []; // all individual measurements (inliers)
    const outlierPoints = []; // predicted outliers only

    function addDays(key, days) {
      const d = new Date(key + "T00:00:00.000Z");
      d.setUTCDate(d.getUTCDate() + days);
      return d.toISOString().slice(0, 10);
    }

    for (let key = startKey; ; key = addDays(key, 1)) {
      const bucket = bucketsMap.get(key);

      const labelStr = new Date(key + "T00:00:00.000Z").toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }
      );

      labels.push(labelStr);

      if (!bucket) {
        values.push(null);
        minMax.push(null);
      } else {
        const vals = bucket.values;

        // Split into inliers / outliers for this day, using GLOBAL robust stats
        const { inliers, outliers } = splitOutliers(vals, globalStats);
        const baseVals = inliers.length ? inliers : vals; // fallback safety

        const sum = baseVals.reduce((acc, v) => acc + v, 0);
        const avg = sum / baseVals.length;

        let localMin = Math.min(...baseVals);
        let localMax = Math.max(...baseVals);

        if (localMin === localMax) {
          const base = Math.abs(localMin || 1);
          const pad = base * 0.02 || 0.02;
          localMin -= pad;
          localMax += pad;
        }

        values.push(avg);
        minMax.push({ min: localMin, max: localMax });

        // Plot inliers as normal tertiary points
        inliers.forEach((v) => {
          rawPoints.push({ x: labelStr, y: v });
        });

        // Plot outliers as separate orange points
        outliers.forEach((v) => {
          outlierPoints.push({ x: labelStr, y: v });
        });
      }

      // IMPORTANT: stop when we reach the end date
      if (key === endKey) break;
    }

    const datasets = [
      {
        label: selectedMetricConfig.label,
        data: values, // daily averages
        tension: 0.25,
        borderColor: PRIMARY_COLOR,
        backgroundColor: PRIMARY_COLOR + "33",
        pointBackgroundColor: PRIMARY_COLOR,
        pointBorderColor: PRIMARY_COLOR,
        pointRadius: 3,
        pointHoverRadius: 4,
        spanGaps: true,
        _minMax: minMax, // used by MinMaxBarsPlugin
      },
    ];

    // ─────────────────────────────────────────────
    // Rough trend / path line (simple linear regression)
    // ─────────────────────────────────────────────
    const numericPoints = values
      .map((v, idx) =>
        v == null || !Number.isFinite(v) ? null : { x: idx, y: v }
      )
      .filter(Boolean);

    if (numericPoints.length >= 2) {
      const n = numericPoints.length;

      let sumX = 0;
      let sumY = 0;
      let sumXY = 0;
      let sumXX = 0;

      for (const p of numericPoints) {
        sumX += p.x;
        sumY += p.y;
        sumXY += p.x * p.y;
        sumXX += p.x * p.x;
      }

      const denom = n * sumXX - sumX * sumX;

      if (denom !== 0) {
        const m = (n * sumXY - sumX * sumY) / denom; // slope
        const b = (sumY - m * sumX) / n; // intercept

        const firstIdx = numericPoints[0].x;
        const lastIdxOverall = values.length - 1; // extend to max X

        const trendValues = values.map((_, idx) => {
          // only draw from first real point onwards, extend to chart end
          if (idx < firstIdx || idx > lastIdxOverall) return null;
          return m * idx + b;
        });

        datasets.push({
          label: `${selectedMetricConfig.label} trend`,
          data: trendValues,
          borderColor: PRIMARY_COLOR,
          borderDash: [6, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          tension: 0,
        });
      }
    }

    // After trend dataset is potentially added:

    if (rawPoints.length) {
      datasets.push({
        label: `${selectedMetricConfig.label} measurements`,
        type: "scatter",
        data: rawPoints,
        parsing: false,
        showLine: false,
        borderColor: TERTIARY_COLOR,
        backgroundColor: TERTIARY_COLOR,
        pointRadius: 3,
        pointHoverRadius: 5,
        hitRadius: 6,
        _isRawPoints: true,
      });
    }

    if (outlierPoints.length) {
      datasets.push({
        label: "Predicted outlier",
        type: "scatter",
        data: outlierPoints,
        parsing: false,
        showLine: false,
        borderColor: OUTLIER_COLOR,
        backgroundColor: OUTLIER_COLOR,
        pointRadius: 4,
        pointHoverRadius: 6,
        hitRadius: 6,
        _isOutlier: true,
      });
    }

    return { labels, datasets };
  }, [sessions, selectedMetricConfig]);

  // Compute y-domain so the axis includes both averages and min/max
  const yDomain = useMemo(() => {
    if (!sessions || !selectedMetricConfig) return null;

    const field = selectedMetricConfig.field;
    const bucketsMap = new Map();

    for (const s of sessions) {
      const raw = s[field];

      if (typeof raw !== "number" || !Number.isFinite(raw)) continue;
      if (!s.timestamp) continue;

      const key = dayKeyFromTimestamp(s.timestamp);
      if (!key) continue;

      if (!bucketsMap.has(key)) {
        bucketsMap.set(key, { values: [] });
      }
      bucketsMap.get(key).values.push(raw);
    }

    const buckets = Array.from(bucketsMap.values());
    if (!buckets.length) return null;

    const all = [];

    for (const b of buckets) {
      const vals = b.values;
      const localMin = Math.min(...vals);
      const localMax = Math.max(...vals);
      const sum = vals.reduce((acc, v) => acc + v, 0);
      const avg = sum / vals.length;

      all.push(localMin, localMax, avg);
    }

    const yMin = Math.min(...all);
    const yMax = Math.max(...all);
    const pad = (yMax - yMin || 1) * 0.08;

    return {
      min: yMin - pad,
      max: yMax + pad,
    };
  }, [sessions, selectedMetricConfig]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false, position: "top" },
        tooltip: {
          enabled: true,
          callbacks: {
            // Use the x-axis label directly (it's already your formatted date)
            title: (items) => {
              if (!items || !items.length) return "";
              return items[0].label || "";
            },
            // Show avg + min/max
            label: (ctx) => {
              const { parsed, dataset, dataIndex } = ctx;
              const y = parsed?.y;
              if (y == null || !Number.isFinite(y)) return "";

              const decimals = selectedMetricConfig?.decimals ?? 2;
              const unit = selectedMetricConfig?.unit
                ? ` ${selectedMetricConfig.unit}`
                : "";

              const fmt = (val) =>
                Number.isFinite(val) ? val.toFixed(decimals) : String(val);

              // Outlier dots (orange)
              if (dataset._isOutlier) {
                return `${
                  selectedMetricConfig?.label || "Value"
                } predicted outlier: ${fmt(y)}${unit} (excluded from averages)`;
              }

              // Normal raw measurement dots (tertiary)
              if (dataset._isRawPoints) {
                return `${
                  selectedMetricConfig?.label || dataset.label
                } measurement: ${fmt(y)}${unit}`;
              }

              const minMax = dataset._minMax?.[dataIndex];

              if (minMax && minMax.min != null && minMax.max != null) {
                return `${selectedMetricConfig?.label || dataset.label}: ${fmt(
                  y
                )}${unit} (min ${fmt(minMax.min)}, max ${fmt(minMax.max)})`;
              }

              return `${selectedMetricConfig?.label || dataset.label}: ${fmt(
                y
              )}${unit}`;
            },
          },
        },

        minMaxBars: {
          lineWidth: 10,
          color: PRIMARY_COLOR + "33",
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          min: yDomain?.min,
          max: yDomain?.max,

          grid: {
            display: true,
            drawTicks: false,
            // Add minor grid lines between major ticks
            // Chart.js 4 uses `border` & `color` on minor
            drawOnChartArea: true,
          },

          // Major ticks = labelled ticks (keep these ON)
          ticks: {
            display: true,
            maxTicksLimit: 10, // labelled ticks
          },

          // Minor ticks = unlabeled ticks → generate extra faint grid lines
          // (Chart.js automatically interpolates)
          minor: {
            ticks: {
              display: false, // do NOT show labels
            },
            grid: {
              color: "#00000015", // faint middle lines
              lineWidth: 0.6,
            },
          },

          title: {
            display: true,
            text: selectedMetricConfig
              ? `${selectedMetricConfig.label} ${
                  selectedMetricConfig.unit
                    ? `(${selectedMetricConfig.unit})`
                    : ""
                }`
              : "",
          },
        },

        x: {
          grid: {
            display: true,
            drawTicks: false,
          },

          ticks: {
            display: true, // KEEP x-axis labels
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
            maxTicksLimit: 10, // labelled ticks only
          },

          minor: {
            ticks: { display: false },
            grid: {
              color: "#00000015",
              lineWidth: 0.5,
            },
          },
        },
      },
    }),
    [selectedMetricConfig, yDomain]
  );

  console.log(chartData);
  return (
    <div className="w-full h-full flex flex-col gap-4 p-4 mx-auto">
      {/* Top bar: back + patient identity + selector */}
      <div className="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center">
        <div className="flex flex-col gap-1">
          {selectedPatient && (
            <p className="muted-text text-sm">
              <span className="font-medium">{selectedPatient.alias}</span>
            </p>
          )}
        </div>

        <Link to={"/patients/profile"}>Profile</Link>
      </div>

      {/* Loading / error states */}
      {loading && (
        <div className="w-full flex justify-center">
          <span className="muted-text">Loading patient results…</span>
        </div>
      )}
      {error && (
        <div className="w-full border-red-400 border-2 rounded-md p-3">
          <p className="text-red-600 font-semibold mb-1">Error</p>
          <p className="muted-text text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && !selectedPatient && (
        <div className="w-full flex justify-center mt-8">
          <span className="muted-text">
            Select a patient to view their results.
          </span>
        </div>
      )}

      {!loading && !error && selectedPatient && (
        <>
          {/* Summary metrics table (like the screenshot) */}
          <div className="w-full border border-greyscale-1 rounded-lg overflow-hidden">
            {/* header row */}
            <div className="grid grid-cols-[48px,2fr,1fr,1fr,1fr] bg-greyscale-0 px-4 py-3 text-sm font-medium text-greyscale-4 border-b border-greyscale-1">
              <span></span>
              <span>Metric</span>
              <span className="text-center">Current (avg)</span>
              <span className="text-center">Measured last</span>
              <span className="text-center">Trend</span>
            </div>

            {/* rows */}
            {metricRows.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setSelectedMetricKey(m.key)}
                className={`grid grid-cols-[48px,2fr,1fr,1fr,1fr] items-center px-4 py-3 text-sm w-full text-left border-t border-greyscale-1 hover:bg-greyscale-0 transition-colors ${
                  selectedMetricKey === m.key ? "bg-greyscale-0" : ""
                } ${!m.hasData ? "opacity-60" : ""}`}
              >
                <span className="flex justify-center">
                  <span
                    className={`w-5 h-5 rounded border flex items-center justify-center ${
                      selectedMetricKey === m.key
                        ? "border-primary bg-primary text-white"
                        : "border-greyscale-2"
                    }`}
                  >
                    {selectedMetricKey === m.key && "✓"}
                  </span>
                </span>
                <span>{m.label}</span>
                <span className="text-center font-medium">
                  {m.hasData && m.currentAvg != null
                    ? `${m.currentAvg.toFixed(m.decimals ?? 2)}${
                        m.unit ? ` ${m.unit}` : ""
                      }`
                    : "--"}
                </span>
                <span className="text-center">
                  {m.hasData ? formatDate(m.measuredLast) : "--"}
                </span>
                <span className="flex justify-center items-center gap-1">
                  <span className="material-symbols-outlined text-lg">
                    {m.trend.icon}
                  </span>
                  <span className="hidden md:inline muted-text text-xs">
                    {m.trend.label}
                  </span>
                </span>
              </button>
            ))}
          </div>

          {/* Time-series chart */}
          <div className="w-full border border-greyscale-1 rounded-lg p-4 flex flex-col gap-2 bg-container">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div>
                <h2 className="subtitle-text">
                  {selectedMetricConfig?.label || "Metric over time"}
                </h2>
                <p className="muted-text text-xs">
                  Session measurements plotted over time for the selected
                  metric.
                </p>
              </div>
            </div>

            {chartData ? (
              <>
                <div className="w-full h-96">
                  <Line
                    data={chartData}
                    options={chartOptions}
                    plugins={[MinMaxBarsPlugin]}
                  />
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-greyscale-3">
                  <div className="flex items-center gap-1">
                    <span
                      className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: TERTIARY_COLOR }}
                    />
                    <span>Measurement</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: OUTLIER_COLOR }}
                    />
                    <span>Predicted outlier (excluded from averages)</span>
                  </div>
                </div>
              </>
            ) : (
              <span className="muted-text text-xs mt-4">
                Not enough data to show this metric.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
